import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useApp } from '../contexts/AppContext';
import { Check, Star, Zap, Crown, KeyRound, ShieldAlert, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const Subscription = () => {
  const { subscription, usage, upgradePlan, manageBilling } = useApp();
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newPlainKey, setNewPlainKey] = useState('');
  const [loadingKeys, setLoadingKeys] = useState(false);

  const plans = [
    {
      id: 'FREE',
      name: 'Free',
      price: '$0',
      period: '/month',
      icon: Star,
      features: [
        '5 documents',
        '5MB file size limit',
        '10 chat sessions/day',
        'Document Q&A',
        'Basic scheduling',
        'Community support'
      ],
      color: 'gray'
    },
    {
      id: 'PRO',
      name: 'Pro',
      price: '$29',
      period: '/month',
      icon: Zap,
      features: [
        '100 documents',
        '50MB file size limit',
        '100 chat sessions/day',
        'Agent Mode (30 runs/month)',
        'Advanced document insights',
        'Schedules + run history',
        'Telegram integration',
        'Priority support'
      ],
      color: 'purple',
      popular: true
    },
    {
      id: 'ENTERPRISE',
      name: 'Enterprise',
      price: '$99',
      period: '/month',
      icon: Crown,
      features: [
        'Unlimited documents',
        '500MB file size limit',
        'Unlimited chat + schedules',
        'Agent Mode (multi-step)',
        'Live web research in Agent',
        'Mindmap generation',
        'Document generation (memo/brief/SOP/action plan)',
        'API keys + Enterprise API access',
        'Custom integrations + dedicated support'
      ],
      color: 'blue'
    }
  ];

  const handleUpgrade = async (planType) => {
    try {
      await upgradePlan(planType);
      toast.success('Redirecting to secure Stripe checkout...');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to start checkout. Please try again.');
    }
  };

  const handleManageBilling = async () => {
    try {
      await manageBilling();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to open billing portal right now.');
    }
  };

  const currentPlan = subscription?.plan_type || 'FREE';

  const loadApiKeys = async () => {
    if (currentPlan !== 'ENTERPRISE') return;
    try {
      setLoadingKeys(true);
      const res = await axios.get('/api/auth/api-keys');
      setApiKeys(res.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setLoadingKeys(false);
    }
  };

  useEffect(() => {
    if (currentPlan === 'ENTERPRISE') {
      loadApiKeys();
    } else {
      setApiKeys([]);
      setNewPlainKey('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlan]);

  const handleCreateApiKey = async () => {
    const name = newKeyName.trim();
    if (!name) {
      toast.error('Please enter an API key name');
      return;
    }

    try {
      const res = await axios.post('/api/auth/api-keys', { name });
      setNewPlainKey(res?.data?.api_key || '');
      setNewKeyName('');
      toast.success('API key created');
      loadApiKeys();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create API key');
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('Revoke this API key?')) return;
    try {
      await axios.delete(`/api/auth/api-keys/${keyId}`);
      toast.success('API key revoked');
      loadApiKeys();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to revoke API key');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Choose Your Plan
        </h1>
        <p className="text-xl text-gray-600">
          Unlock the full potential of Lumina AI with advanced features
        </p>
      </div>

      {/* Lumina Capabilities Summary */}
      <div className="card mb-8 border-blue-200 bg-blue-50/40">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">What Lumina can do today</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
          <p>• Chat with uploaded documents and extract actionable insights</p>
          <p>• Auto-create and run schedules with timezone support</p>
          <p>• Telegram link + notifications + file ingestion</p>
          <p>• Agent Mode for research (Pro: 30 runs/month, Enterprise: higher limits)</p>
          <p>• Generate mindmaps and business documents from agent runs (Enterprise)</p>
          <p>• API key management and integration endpoints (Enterprise)</p>
        </div>
      </div>

      {/* Current Subscription Status */}
      {subscription && (
        <div className="card mb-8 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-purple-900">Current Plan</h3>
              <p className="text-purple-700 mt-1">
                You're currently on the <strong>{currentPlan}</strong> plan
              </p>
              {subscription.end_date && (
                <p className="text-sm text-purple-600 mt-1">
                  {subscription.is_active ? 'Renews' : 'Expires'} on{' '}
                  {new Date(subscription.end_date).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-purple-900">
                {plans.find(p => p.id === currentPlan)?.price || '$0'}
              </div>
              <div className="text-sm text-purple-600 mb-2">per month</div>
              {currentPlan !== 'FREE' && (
                <button
                  onClick={handleManageBilling}
                  className="px-3 py-1.5 text-sm rounded-md bg-white border border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  Manage Billing
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Usage Statistics */}
      {usage && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="card text-center">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Documents</h4>
            <div className="text-3xl font-bold text-blue-600 mb-1">
              {usage.documents_used}
            </div>
            <p className="text-gray-600 text-sm">
              of {usage.documents_limit === null ? '∞' : usage.documents_limit} used
            </p>
          </div>
          
          <div className="card text-center">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Chat Sessions</h4>
            <div className="text-3xl font-bold text-green-600 mb-1">
              {usage.chat_sessions_used}
            </div>
            <p className="text-gray-600 text-sm">today</p>
          </div>
          
          <div className="card text-center">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Storage</h4>
            <div className="text-3xl font-bold text-purple-600 mb-1">
              {Math.round((usage.storage_used || 0) / 1024 / 1024)}
            </div>
            <p className="text-gray-600 text-sm">MB used</p>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {plans.map((plan) => {
          const isCurrentPlan = plan.id === currentPlan;
          const IconComponent = plan.icon;
          
          return (
            <div
              key={plan.id}
              className={`card relative ${
                plan.popular ? 'ring-2 ring-purple-500 transform scale-105' : ''
              } ${isCurrentPlan ? 'bg-gray-50 border-gray-300' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}
              
              {isCurrentPlan && (
                <div className="absolute -top-4 right-4">
                  <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Current
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6">
                <div className={`mx-auto w-16 h-16 bg-${plan.color}-100 rounded-full flex items-center justify-center mb-4`}>
                  <IconComponent className={`h-8 w-8 text-${plan.color}-600`} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="flex items-center justify-center">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-600 ml-1">{plan.period}</span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                onClick={() => !isCurrentPlan && handleUpgrade(plan.id)}
                disabled={isCurrentPlan}
                className={`w-full py-3 px-6 rounded-lg font-medium transition-colors duration-200 ${
                  isCurrentPlan
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : plan.popular
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                {isCurrentPlan ? 'Current Plan' : `Upgrade to ${plan.name}`}
              </button>
            </div>
          );
        })}
      </div>

      {/* API Access */}
      <div className="mt-12 card border-purple-200 bg-purple-50/40">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-purple-600" />
              API Access
            </h3>
            <p className="text-gray-600 mt-1">
              Enterprise unlocks API access plus advanced Agent Mode capabilities (live web research, mindmaps, and generated documents).
            </p>
          </div>
          {currentPlan !== 'ENTERPRISE' && (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
              <ShieldAlert className="h-4 w-4" />
              Enterprise required
            </span>
          )}
        </div>

        {currentPlan === 'ENTERPRISE' ? (
          <div className="mt-6 space-y-4">
            {newPlainKey && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                <p className="text-sm text-green-800 font-medium">New API key (shown once)</p>
                <code className="block mt-1 text-xs break-all text-green-900">{newPlainKey}</code>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <input
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="Key name (e.g. production-integration)"
                className="input-field"
              />
              <button onClick={handleCreateApiKey} className="btn-primary whitespace-nowrap">
                Create API Key
              </button>
            </div>

            <div className="space-y-2">
              {loadingKeys ? (
                <p className="text-sm text-gray-500">Loading API keys...</p>
              ) : apiKeys.length === 0 ? (
                <p className="text-sm text-gray-500">No API keys yet.</p>
              ) : (
                apiKeys.map((k) => (
                  <div key={k.id} className="flex items-center justify-between p-3 rounded-lg bg-white border border-gray-200">
                    <div>
                      <p className="font-medium text-gray-900">{k.name}</p>
                      <p className="text-xs text-gray-500">Prefix: {k.key_prefix} • Created {new Date(k.created_at).toLocaleDateString()}</p>
                    </div>
                    <button
                      onClick={() => handleRevokeApiKey(k.id)}
                      className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" /> Revoke
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <p className="text-sm text-gray-600 mb-3">
              Upgrade to Enterprise to create and manage API keys for integrations and automation.
            </p>
            <button onClick={() => handleUpgrade('ENTERPRISE')} className="btn-primary">
              Upgrade to Enterprise
            </button>
          </div>
        )}
      </div>

      {/* FAQ or Additional Info */}
      <div className="mt-16 text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">
          Need help choosing?
        </h3>
        <p className="text-gray-600 mb-6">
          All plans include our core AI features. Upgrade anytime to get more capacity and advanced features.
        </p>
        <div className="space-y-2 text-sm text-gray-500">
          <p>• All plans include 99.9% uptime guarantee</p>
          <p>• Cancel or change your plan anytime</p>
          <p>• Secure payment processing</p>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
